import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, User } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { adminService } from '../services/api'

const UserForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const isEdit = !!id

  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    role: 'customer'
  })

  const [errors, setErrors] = useState({})

  // Validation function
  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!userForm.name.trim()) {
      newErrors.name = 'الاسم مطلوب'
    } else if (userForm.name.trim().length < 2) {
      newErrors.name = 'الاسم يجب أن يكون على الأقل حرفين'
    }

    // Email validation
    if (!userForm.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
      newErrors.email = 'يرجى إدخال عنوان بريد إلكتروني صالح'
    }

    // Password validation (required for new users)
    if (!isEdit) {
      if (!userForm.password) {
        newErrors.password = 'كلمة المرور مطلوبة'
      } else if (userForm.password.length < 6) {
        newErrors.password = 'كلمة المرور يجب أن تكون على الأقل 6 أحرف'
      }
    } else if (userForm.password && userForm.password.length > 0) {
      // For edit mode, only validate if password is provided
      if (userForm.password.length < 6) {
        newErrors.password = 'كلمة المرور يجب أن تكون على الأقل 6 أحرف'
      }
    }

    // Phone validation (optional but format check if provided)
    if (userForm.phone && !/^\+?[\d\s\-\(\)]+$/.test(userForm.phone)) {
      newErrors.phone = 'يرجى إدخال رقم هاتف صالح'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Fetch user data if editing
  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user', id],
    queryFn: () => adminService.getUser(id),
    enabled: !!id,
    retry: false
  })

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (userData) => adminService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users'])
      navigate('/admin', { state: { activeTab: 'users' } })
    },
    onError: (error) => {
      console.error('Create user error:', error)
      const errorData = error.response?.data
      
      if (errorData?.code === 'VALIDATION_ERROR') {
        // Handle validation errors from server
        const serverErrors = {}
        errorData.details?.forEach(detail => {
          serverErrors[detail.field] = detail.message
        })
        setErrors(serverErrors)
      } else if (errorData?.code === 'USER_EXISTS') {
        // Handle user already exists errors
        const serverErrors = {}
        errorData.details?.forEach(detail => {
          if (detail.field === 'email') {
            serverErrors.email = 'هذا البريد الإلكتروني مستخدم بالفعل'
          } else if (detail.field === 'username') {
            serverErrors.username = 'اسم المستخدم هذا مستخدم بالفعل'
          }
        })
        setErrors(serverErrors)
      } else {
        alert('فشل في إنشاء المستخدم: ' + (errorData?.message || error.message))
      }
    }
  })

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }) => adminService.updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users'])
      queryClient.invalidateQueries(['user', id])
      navigate('/admin', { state: { activeTab: 'users' } })
    },
    onError: (error) => {
      console.error('Update user error:', error)
      const errorData = error.response?.data
      
      if (errorData?.code === 'VALIDATION_ERROR') {
        // Handle validation errors from server
        const serverErrors = {}
        errorData.details?.forEach(detail => {
          serverErrors[detail.field] = detail.message
        })
        setErrors(serverErrors)
      } else {
        alert('فشل في تحديث المستخدم: ' + (errorData?.message || error.message))
      }
    }
  })

  // Load user data when editing
  useEffect(() => {
    if (user) {
      setUserForm({
        name: user.name || '',
        email: user.email || '',
        password: '', // Don't pre-fill password
        phone: user.phone || '',
        address: user.address || '',
        role: user.role || 'customer'
      })
    }
  }, [user])

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Clear previous errors
    setErrors({})
    
    // Validate form
    if (!validateForm()) {
      return
    }

    // Prepare data for submission
    const submitData = {
      name: userForm.name.trim(),
      email: userForm.email.trim().toLowerCase(),
      phone: userForm.phone.trim(),
      address: userForm.address.trim(),
      role: userForm.role
    }

    // Only include password if it's provided
    if (userForm.password) {
      submitData.password = userForm.password
    }

    if (isEdit) {
      updateUserMutation.mutate({ userId: id, userData: submitData })
    } else {
      createUserMutation.mutate(submitData)
    }
  }

  const handleInputChange = (field, value) => {
    setUserForm(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (userError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
          <h3 className="text-red-800 font-semibold">خطأ في تحميل بيانات المستخدم</h3>
          <p className="text-red-600 text-sm mt-2">
            {userError?.message || 'حدث خطأ غير متوقع'}
          </p>
          <button
            onClick={() => navigate('/admin', { state: { activeTab: 'users' } })}
            className="mt-3 btn btn-outline btn-sm"
          >
            العودة إلى قائمة المستخدمين
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/admin', { state: { activeTab: 'users' } })}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center space-x-2">
                  <User className="w-6 h-6 text-blue-600" />
                  <h1 className="text-xl font-semibold text-gray-900">
                    {isEdit ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}
                  </h1>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الاسم <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : ''}`}
                  autoComplete="name"
                  placeholder="أدخل الاسم الكامل (حرفين على الأقل)"
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.email ? 'border-red-500' : ''}`}
                  required
                  autoComplete="email"
                  placeholder="أدخل البريد الإلكتروني"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  كلمة المرور {isEdit && '(اتركها فارغة للاحتفاظ بالحالية)'} {!isEdit && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.password ? 'border-red-500' : ''}`}
                  required={!isEdit}
                  autoComplete={isEdit ? "new-password" : "new-password"}
                  placeholder={!isEdit ? "6 أحرف على الأقل" : ""}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الدور
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="customer">عميل</option>
                  <option value="admin">مدير</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف
                </label>
                <input
                  type="tel"
                  value={userForm.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.phone ? 'border-red-500' : ''}`}
                  autoComplete="tel"
                  placeholder="أدخل رقم الهاتف (اختياري)"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان
                </label>
                <textarea
                  value={userForm.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  autoComplete="address"
                  placeholder="أدخل العنوان الكامل (اختياري)"
                  rows="3"
                />
              </div>
            </div>



            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin', { state: { activeTab: 'users' } })}
                className="btn btn-outline"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={createUserMutation.isLoading || updateUserMutation.isLoading}
                className="btn btn-primary"
              >
                <Save className="w-4 h-4 mr-2" />
                {(createUserMutation.isLoading || updateUserMutation.isLoading) ? 'حفظ...' : 'حفظ'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default UserForm
